import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Lock, Heart, Shield, BookOpen, UsersThree, Sun, Moon } from 'phosphor-react';
import { useAuth } from '../contexts/AuthContext';
import { useVelaTheme } from '../contexts/VelaThemeContext';
const Landing = () => {
  const { hasPasscode, isLoading } = useAuth();
  const { theme, toggleTheme } = useVelaTheme();
  const navigate = useNavigate();
  const logoSrc = theme === 'dark' ? '/aravela-logo-dark.png' : '/aravela-logo-light.png';

  const handleBeginJourney = () => {
    // Smart routing based on user state
    if (isLoading) return;
    if (hasPasscode) {
      navigate('/unlock');
    } else {
      navigate('/start');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-stone-100 noise-texture">
      {/* Theme toggle + Support (top-right) */}
      <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
        <button
          onClick={() => navigate('/support')}
          className="bg-white border border-stone-200 rounded-full px-3 py-2 hover:bg-stone-50 transition-colors shadow-sm inline-flex items-center gap-1.5 text-sm text-stone-700"
          data-testid="landing-support-btn"
          aria-label="Support AraVela"
          title="Support AraVela"
        >
          <Heart size={16} weight="duotone" />
          <span className="hidden sm:inline">Support AraVela</span>
        </button>
        <button
          onClick={toggleTheme}
          className="bg-white border border-stone-200 rounded-full p-2 hover:bg-stone-50 transition-colors shadow-sm"
          data-testid="vela-theme-toggle"
          aria-label="Toggle theme"
          title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {theme === 'dark' ? <Sun size={20} weight="duotone" /> : <Moon size={20} weight="duotone" />}
        </button>
      </div>
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20 mix-blend-multiply"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1759710465485-7cb5fe4c57cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHx3YXJtJTIwY296eSUyMHJvb20lMjBpbnRlcmlvciUyMHN1bmxpZ2h0fGVufDB8fHx8MTc3MTkzMDUzMHww&ixlib=rb-4.1.0&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        
        <div className="relative max-w-4xl mx-auto px-6 py-20 text-center">
          <div className="mb-6 flex justify-center">
            <img
              src={logoSrc}
              alt="AraVela logo"
              className="w-32 h-32 sm:w-40 sm:h-40 object-contain rounded-full"
              data-testid="landing-logo"
            />
          </div>
          
          <h1 
            className="text-5xl sm:text-6xl lg:text-7xl font-serif font-semibold text-stone-800 mb-4"
            data-testid="landing-title"
          >
            AraVela
          </h1>
          <p className="text-lg sm:text-xl text-stone-600 mb-2 font-sans">
            Your private sanctuary with Vela
          </p>
          <p className="text-base text-stone-500 max-w-2xl mx-auto mb-6 leading-relaxed">
            A locked journal and supportive companion for emotional reflection.
            Your thoughts, your ownership, encrypted on your device.
          </p>

          <div className="max-w-xl mx-auto mb-10 px-4">
            <div className="bg-stone-100/50 border border-stone-200/50 rounded-2xl px-6 py-4">
              <p className="text-sm text-stone-600 leading-relaxed text-center">
                AraVela is currently in an early release phase. Features will continue to evolve as safety tools, 
                follow-up support, and connection systems grow. Your experience helps shape its development.
              </p>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={handleBeginJourney}
              disabled={isLoading}
              data-testid="get-started-btn"
              className="inline-block bg-primary text-white rounded-full px-10 py-4 font-medium text-lg hover:shadow-soft transition-all duration-300 active:scale-95 disabled:opacity-50"
            >
              Begin Your Journey
            </button>
            <button
              onClick={() => navigate('/ara')}
              data-testid="enter-ara-btn"
              className="inline-block bg-white text-stone-800 border border-stone-200 rounded-full px-8 py-4 font-medium hover:bg-stone-50 hover:shadow-sm transition-all duration-300 active:scale-95"
            >
              Supporting someone? Enter Ara
            </button>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 gap-8">
          <FeatureCard
            icon={<Lock size={32} weight="duotone" className="text-primary" />}
            title="Encrypted on Your Device"
            description="Your diary entries, patterns, and session history are encrypted locally using Web Crypto (AES-GCM) with a key derived from your passcode. Without it, no one — including us — can read your data."
          />
          <FeatureCard
            icon={<Heart size={32} weight="duotone" className="text-secondary" />}
            title="Your Autonomy"
            description="Nothing is saved unless you choose to. Tap Save to Diary when a session matters, delete entries anytime, and end sessions on your terms. Vela is a companion, not therapy — you're always in the driver's seat."
          />
          <FeatureCard
            icon={<Shield size={32} weight="duotone" className="text-primary" />}
            title="No Recovery, No Backdoor"
            description="Forgot your passcode? Your data stays sealed. That's real security — we can't help you back in, and no one else can either. Your data belongs only to you."
          />
          <FeatureCard
            icon={<BookOpen size={32} weight="duotone" className="text-secondary" />}
            title="Your Rhythm, Your Choice"
            description="Nothing leaves your device. In Vela, tap Save to Diary when an entry matters — that's the one deliberate moment it's persisted, encrypted with your passcode. In Ara, your conversations save automatically on your device as they develop; Save Reflection is an optional way to mark something meaningful you'd like to return to."
          />
        </div>

        {/* Ara — Available Now */}
        <div className="mt-12">
          <div
            className="bg-white/60 border border-stone-200 rounded-3xl p-8 text-center"
            data-testid="ara-overview-card"
            aria-label="Ara overview"
          >
            <div className="inline-block bg-primary/15 text-primary text-xs uppercase tracking-wider px-3 py-1 rounded-full mb-4 font-medium">
              Available Now
            </div>
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-3">
              <UsersThree size={24} weight="duotone" className="text-primary" />
            </div>
            <h3 className="text-xl font-serif font-semibold text-stone-800 mb-2">
              Ara — a sanctuary for the ones who care
            </h3>
            <p className="text-sm text-stone-600 max-w-lg mx-auto leading-relaxed mb-5">
              Ara is a private, independent companion for people supporting someone else —
              partners, family, friends, caregivers, peers. It helps you reflect,
              communicate compassionately, understand relational dynamics without
              diagnosing anyone, and care for your own needs and boundaries.
            </p>
            <button
              onClick={() => navigate('/ara')}
              className="inline-block bg-white border border-stone-300 rounded-full px-6 py-2 text-sm font-medium text-stone-800 hover:bg-stone-50 transition-colors shadow-sm"
              data-testid="landing-enter-ara-card-btn"
            >
              Enter Ara
            </button>
            <p className="text-xs text-stone-500 mt-5 max-w-md mx-auto leading-relaxed">
              Looking ahead: Vela and Ara may eventually support carefully controlled,
              consent-based sharing of selected support preferences between the two
              companions. That bridge is not yet available — Ara works today as its
              own independent companion.
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="max-w-6xl mx-auto px-6 py-12 border-t border-stone-200">
        <div className="flex flex-wrap justify-center gap-6 text-sm text-stone-500">
          <Link to="/privacy" className="hover:text-primary transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-primary transition-colors">Terms of Use</Link>
          <Link to="/contact" className="hover:text-primary transition-colors">Contact</Link>
        </div>
        <p className="text-center text-xs text-stone-400 mt-4">
          AraVela is not therapy and does not provide medical advice.
        </p>
      </div>
    </div>
  );
};

const FeatureCard = ({ icon, title, description }) => {
  return (
    <div className="bg-white rounded-3xl p-8 shadow-sm hover:shadow-soft transition-all duration-300">
      <div className="mb-4">{icon}</div>
      <h3 className="text-xl font-serif font-semibold text-stone-800 mb-2">{title}</h3>
      <p className="text-stone-600 leading-relaxed">{description}</p>
    </div>
  );
};

export default Landing;
